import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from 'react-query';
import {
    FiFile, FiImage, FiFileText, FiGrid, FiCode,
    FiFilm, FiMusic, FiArchive, FiDownload, FiEye,
    FiCheckCircle, FiCircle
} from 'react-icons/fi';
import { formatBytes } from '../../../helpers/formatters';
import { API_AUTH_TYPES, API_ROUTES, ApiRequest } from '../../../api';
import FilePreview from './DocumentManager/FilePreview';
import Highlighter from 'react-highlight-words';

const DocumentCard = ({ document, authType = API_AUTH_TYPES.USER, query = '' }) => {
    const { household_id: householdId } = useParams();
    const [showPreview, setShowPreview] = useState(false);
    const [signedUrl, setSignedUrl] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const documentsApi = new ApiRequest(API_ROUTES.DOCUMENTS, authType);
    const isAdvisor = authType === API_AUTH_TYPES.ADVISOR;
    const queryClient = useQueryClient();

    const toggleReviewedMutation = useMutation({
        mutationFn: (reviewed) => documentsApi.patch({
            endpoint: "update",
            params: [document?.id],
            body: {
                reviewed,
                ...(isAdvisor && { householdId: document?.household_id })
            }
        }),
        onSuccess: () => {
            queryClient.invalidateQueries(['hh-search', householdId]);
            queryClient.invalidateQueries(['documents']);
            queryClient.invalidateQueries('advisor-dashboard');
        }
    });

    if (!document) {
        return null;
    }

    const getFileIcon = (mimeType, fileName) => {
        const extension = fileName?.split('.').pop()?.toLowerCase();

        if (['pdf', 'doc', 'docx', 'txt', 'rtf'].includes(extension)) {
            return <FiFileText className="h-6 w-6 text-blue-500" />;
        }
        if (['xls', 'xlsx', 'csv'].includes(extension)) {
            return <FiGrid className="h-6 w-6 text-green-500" />;
        }
        if (mimeType?.startsWith('image/')) {
            return <FiImage className="h-6 w-6 text-purple-500" />;
        }
        if (mimeType?.startsWith('video/')) {
            return <FiFilm className="h-6 w-6 text-red-500" />;
        }
        if (mimeType?.startsWith('audio/')) {
            return <FiMusic className="h-6 w-6 text-pink-500" />;
        }
        if (mimeType?.startsWith('application/json') ||
            mimeType?.startsWith('text/javascript') ||
            mimeType?.startsWith('text/html')) {
            return <FiCode className="h-6 w-6 text-yellow-500" />;
        }
        if (mimeType?.startsWith('application/zip') ||
            mimeType?.startsWith('application/x-rar-compressed')) {
            return <FiArchive className="h-6 w-6 text-orange-500" />;
        }
        return <FiFile className="h-6 w-6 text-gray-500" />;
    };

    const fetchSignedUrl = async () => {
        if (!document.id) return null;

        setIsLoading(true);
        try {
            const params = isAdvisor && householdId ?
                [householdId, document.id] :
                [document.id];

            const response = await documentsApi.get({
                endpoint: "get",
                params
            });
            return response.signed_url;
        } catch (error) {
            console.error('Error fetching signed URL:', error);
            return null;
        } finally {
            setIsLoading(false);
        }
    };

    const handlePreview = async () => {
        const url = await fetchSignedUrl();
        if (url) {
            setSignedUrl(url);
            setShowPreview(true);
        }
    };

    const handleDownload = async () => {
        const url = await fetchSignedUrl();
        if (url) {
            const link = window.document.createElement('a');
            link.href = url;
            link.download = document.original_name || document.name || 'download';
            link.click();
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Unknown date';
        try {
            return new Date(dateString).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch {
            return 'Unknown date';
        }
    };

    const handleToggleReviewed = (e) => {
        e.stopPropagation();
        toggleReviewedMutation.mutate(!document.reviewed);
    };

    // Prepare search words for highlighting
    const searchWords = query && query.trim() !== '' ?
        (Array.isArray(query) ? query : [query]) :
        [];

    return (
        <>
            <div className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start space-x-3">
                    {/* File Icon */}
                    <div className="flex-shrink-0 p-2 bg-gray-50 rounded-lg">
                        {getFileIcon(document.mime_type, document.original_name || document.name)}
                    </div>

                    {/* Document Info */}
                    <div className="flex-1 min-w-0">
                        {searchWords.length > 0 && (document.name || document.original_name) ? (
                            <h3 className="text-lg font-semibold text-gray-900 truncate">
                                <Highlighter
                                    highlightClassName="bg-yellow-200 text-yellow-900"
                                    searchWords={searchWords}
                                    autoEscape={true}
                                    textToHighlight={document.name || document.original_name || 'Unknown Document'}
                                />
                            </h3>
                        ) : (
                            <h3 className="text-lg font-semibold text-gray-900 truncate">
                                {document.name || document.original_name || 'Unknown Document'}
                            </h3>
                        )}

                        <div className="flex flex-wrap items-center gap-3 mt-2">
                            <span className="text-sm text-gray-500">
                                {formatBytes(document.file_size)}
                            </span>
                            <span className="text-sm text-gray-500">
                                {formatDate(document.created_at || document.uploaded_at)}
                            </span>
                            {document.document_type && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                    {searchWords.length > 0 && document.document_type ? (
                                        <Highlighter
                                            highlightClassName="bg-yellow-200 text-yellow-900"
                                            searchWords={searchWords}
                                            autoEscape={true}
                                            textToHighlight={document.document_type}
                                        />
                                    ) : (
                                        document.document_type
                                    )}
                                </span>
                            )}
                        </div>


                    </div>

                    {/* Action Buttons */}
                    <div className="flex space-x-2">
                        <button
                            onClick={handlePreview}
                            disabled={isLoading}
                            className="p-2 rounded-full hover:bg-gray-100 text-gray-500 transition-colors"
                            title="Preview Document"
                        >
                            {isLoading ? (
                                <div className="h-5 w-5 animate-spin border-2 border-gray-300 border-t-gray-600 rounded-full" />
                            ) : (
                                <FiEye className="h-5 w-5" />
                            )}
                        </button>

                        {isAdvisor && (
                            <button
                                onClick={handleToggleReviewed}
                                className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                                title={document.reviewed ? 'Mark unreviewed' : 'Mark reviewed'}
                            >
                                {document.reviewed ? (
                                    <FiCheckCircle className="h-5 w-5 text-green-500" />
                                ) : (
                                    <FiCircle className="h-5 w-5 text-gray-400" />
                                )}
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Preview Modal */}
            {showPreview && signedUrl && (
                <FilePreview
                    file={{ ...document, url: signedUrl }}
                    onClose={() => {
                        setShowPreview(false);
                        setSignedUrl(null);
                    }}
                    onDownload={handleDownload}
                />
            )}
        </>
    );
};

export default DocumentCard; 